/**
 * The deck's physical controls: start/stop, the 33/45 pads, power, the strobe
 * illuminator and the pitch fader.
 *
 * Each returns the material its indicator light uses, so the runtime can light
 * them without holding references to the meshes.
 */

import * as THREE from 'three';
import { CONTROLS, DIM, FADER } from '../../geometry/dims.ts';
import { decalTexture } from '../../textures/decals.ts';
import { ACCENT, type DeckMaterials } from '../materials.ts';
import { cyl, hitVolume, roundedBoxGeometry } from '../shapes.ts';
import type { AddHit } from './hits.ts';

export interface DeckLeds {
  start: THREE.MeshStandardMaterial;
  s33: THREE.MeshStandardMaterial;
  s45: THREE.MeshStandardMaterial;
  power: THREE.MeshStandardMaterial;
  strobe: THREE.MeshBasicMaterial;
}

function buildStartStop(root: THREE.Group, M: DeckMaterials, addHit: AddHit) {
  const { x, z, r } = CONTROLS.start;
  const well = new THREE.Mesh(cyl(r + 0.45, r + 0.45, 0.35, 48), M.shellWhiteSoft);
  well.position.set(x, DIM.top - 0.1, z);
  root.add(well);

  const button = new THREE.Mesh(cyl(r, r + 0.05, 0.7, 48), M.shellWhite);
  button.position.set(x, DIM.top + 0.3, z);
  button.castShadow = true;
  root.add(addHit(button, { type: 'start' }));

  const ring = new THREE.Mesh(new THREE.TorusGeometry(r + 0.25, 0.13, 10, 64), M.accent.clone());
  ring.rotation.x = -Math.PI / 2;
  ring.position.set(x, DIM.top + 0.16, z);
  root.add(ring);
  return ring.material;
}

function buildSpeedButton(
  root: THREE.Group,
  M: DeckMaterials,
  addHit: AddHit,
  label: string,
  x: number,
  value: number,
) {
  const g = new THREE.Group();
  g.position.set(x, DIM.top, CONTROLS.speed.z);
  root.add(g);

  const { w, d } = CONTROLS.speed;
  const pad = new THREE.Mesh(roundedBoxGeometry(w, d, 0.7, 0.7, 0.22, 10), M.shellWhite);
  pad.castShadow = true;
  g.add(addHit(pad, { type: 'speed', value }));

  const led = new THREE.Mesh(cyl(0.28, 0.28, 0.1, 16), M.accent.clone());
  led.position.set(-1.5, 0.72, 0);
  g.add(led);

  const text = new THREE.Mesh(
    new THREE.PlaneGeometry(2.4, 1.2),
    M.decal(decalTexture(label, { size: 70, letterSpacing: '0.02em' })),
  );
  text.rotation.x = -Math.PI / 2;
  text.position.set(0.5, 0.73, 0);
  g.add(text);
  return led.material;
}

function buildPower(root: THREE.Group, M: DeckMaterials, addHit: AddHit) {
  const { x, z, r } = CONTROLS.power;
  const button = new THREE.Mesh(cyl(r - 0.05, r, 0.6, 32), M.shellWhite);
  button.position.set(x, DIM.top + 0.25, z);
  button.castShadow = true;
  root.add(addHit(button, { type: 'power' }));

  const led = new THREE.Mesh(cyl(0.32, 0.32, 0.12, 16), M.accent.clone());
  led.position.set(x, DIM.top + 0.58, z);
  root.add(led);
  return led.material;
}

/** The lamp beside the platter that makes the strobe dots readable. */
function buildStrobeLamp(root: THREE.Group, M: DeckMaterials) {
  const lamp = new THREE.Mesh(roundedBoxGeometry(1.4, 2.6, 0.6, 0.3, 0.16, 6), M.shellWhite);
  lamp.position.set(-23.2, DIM.top, -4.0);
  root.add(lamp);

  const led = new THREE.Mesh(
    new THREE.PlaneGeometry(0.46, 1.35),
    new THREE.MeshBasicMaterial({
      color: ACCENT,
      transparent: true,
      opacity: 0.12,
      depthWrite: false,
    }),
  );
  led.rotation.x = -Math.PI / 2;
  led.position.set(-23.2, DIM.top + 0.62, -4.0);
  root.add(led);
  return led.material;
}

/** The pitch fader. The knob group is returned so the runtime can slide it. */
export function buildFader(root: THREE.Group, M: DeckMaterials, addHit: AddHit): THREE.Group {
  const well = new THREE.Mesh(
    roundedBoxGeometry(3.6, FADER.len + 3.0, 0.5, 1.2, 0.2, 10),
    M.shellWhiteSoft,
  );
  well.position.set(FADER.x, DIM.top - 0.16, FADER.z);
  root.add(well);

  const slot = new THREE.Mesh(
    roundedBoxGeometry(0.7, FADER.len + 0.6, 0.3, 0.34, 0.12, 8),
    M.darkPlastic,
  );
  slot.position.set(FADER.x, DIM.top + 0.02, FADER.z);
  root.add(slot);

  const knob = new THREE.Group();
  knob.position.set(FADER.x, DIM.top + 0.36, FADER.z);
  root.add(knob);

  const body = new THREE.Mesh(roundedBoxGeometry(2.9, 1.7, 0.95, 0.34, 0.2, 8), M.shellWhite);
  body.castShadow = true;
  knob.add(body);

  const line = new THREE.Mesh(new THREE.BoxGeometry(3.0, 0.08, 0.14), M.aluminiumDark);
  line.position.y = 0.96;
  knob.add(line);

  const grab = hitVolume(new THREE.BoxGeometry(5.0, 3.0, FADER.len + 3.0));
  grab.position.set(FADER.x, DIM.top + 1.0, FADER.z);
  root.add(addHit(grab, { type: 'pitch' }));

  const tickGeo = new THREE.PlaneGeometry(1.5, 0.11);
  const tickMat = new THREE.MeshStandardMaterial({ color: 0x8a948f, roughness: 0.7 });
  for (let i = -4; i <= 4; i++) {
    const tick = new THREE.Mesh(tickGeo, tickMat);
    tick.rotation.x = -Math.PI / 2;
    tick.position.set(FADER.x - 2.6, DIM.top + 0.02, FADER.z + (i / 4) * (FADER.len / 2));
    tick.scale.x = i === 0 ? 1.25 : 0.7;
    root.add(tick);
  }
  return knob;
}

export function buildControls(root: THREE.Group, M: DeckMaterials, addHit: AddHit): DeckLeds {
  return {
    start: buildStartStop(root, M, addHit),
    s33: buildSpeedButton(root, M, addHit, '33', CONTROLS.speed.x33, 33),
    s45: buildSpeedButton(root, M, addHit, '45', CONTROLS.speed.x45, 45),
    power: buildPower(root, M, addHit),
    strobe: buildStrobeLamp(root, M),
  };
}
